
import { useState, useEffect } from "react";
import { AuthProviderProps } from "./auth/types";
import { useAuthSession } from "@/hooks/useAuthSession";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { supabase } from "@/integrations/supabase/client";
import { AuthStateManager } from "./auth/state/AuthStateManager";
import { useGlobalThemeLoader } from "@/hooks/useGlobalThemeLoader";

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { user, isLoading } = useAuthSession();
  const [retryCount, setRetryCount] = useState(0);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  
  // Load user's theme preferences globally
  useGlobalThemeLoader();

  // Add additional auth verification if loading takes too long
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    
    if (isLoading && !hasCheckedAuth && retryCount < 3) {
      console.log('Auth still loading, will verify after timeout');
      timeout = setTimeout(async () => {
        try {
          console.log('Verifying auth state...');
          const authManager = AuthStateManager.getInstance();
          authManager.startAutoRefresh();
          
          const { data } = await supabase.auth.getSession();
          console.log('Manual session check:', data.session ? 'Session found' : 'No session');
          
          setRetryCount(prev => prev + 1);
        } catch (error) {
          console.error('Error in auth verification:', error);
        } finally {
          setHasCheckedAuth(true);
        }
      }, 2000); // Wait 2 seconds before checking auth state
    }
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading, hasCheckedAuth, retryCount]);

  console.log('AuthProvider rendering, isLoading:', isLoading, 'user:', user?.id);

  if (isLoading && retryCount < 2) {
    return <LoadingSpinner />;
  }

  // If still loading after retries, proceed anyway to avoid permanent loading state
  return children(user);
};
